import * as THREE from 'three';
import { materialPool } from './MaterialPool';
import type { CollisionSystem } from './CollisionSystem';

// ============================================================
// Tracer rounds — glowing cylinders fired from the aircraft
// ============================================================

const TRACER_SPEED = 800;
const TRACER_LIFETIME = 0.7;
const TRACER_LENGTH = 60;
const TRACER_RADIUS = 0.18;
const FIRE_INTERVAL = 0.05;
const SPREAD = 0.035;

// Shared geometry — cylinder along Y, we orient it per tracer
const sharedGeometry = new THREE.CylinderGeometry(TRACER_RADIUS, TRACER_RADIUS, TRACER_LENGTH, 4, 1);
sharedGeometry.rotateX(Math.PI / 2); // align along Z so lookAt works

interface Tracer {
  readonly mesh: THREE.Mesh;
  readonly velocity: THREE.Vector3;
  age: number;
}

const _forward = new THREE.Vector3();
const _right = new THREE.Vector3();
const _up = new THREE.Vector3();
const _tracerDir = new THREE.Vector3();

export class TracerSystem {
  private readonly scene: THREE.Scene;
  private readonly tracers: Tracer[] = [];
  private fireTimer = 0;
  private collisionSystem: CollisionSystem | null = null;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  setCollisionSystem(cs: CollisionSystem): void {
    this.collisionSystem = cs;
  }

  update(aircraft: THREE.Object3D, firing: boolean, dt: number, hitCheck?: (pos: THREE.Vector3) => boolean): void {
    if (firing) {
      this.fireTimer -= dt;
      if (this.fireTimer <= 0) {
        this.fireTimer = FIRE_INTERVAL;
        this.spawnTracer(aircraft);
      }
    } else {
      this.fireTimer = 0;
    }

    for (let i = this.tracers.length - 1; i >= 0; i--) {
      const t = this.tracers[i];
      t.age += dt;

      if (t.age >= TRACER_LIFETIME) {
        this.scene.remove(t.mesh);
        materialPool.release(t.mesh.material as THREE.MeshBasicMaterial);
        this.tracers.splice(i, 1);
        continue;
      }

      // Move
      t.mesh.position.addScaledVector(t.velocity, dt);

      // Building collision — bullet stops on impact
      if (this.collisionSystem) {
        const dir = _tracerDir.copy(t.velocity).normalize();
        if (this.collisionSystem.isBlockedByBuilding(t.mesh.position, dir, TRACER_SPEED * dt)) {
          this.scene.remove(t.mesh);
          materialPool.release(t.mesh.material as THREE.MeshBasicMaterial);
          this.tracers.splice(i, 1);
          continue;
        }
      }

      // Hit check
      if (hitCheck && hitCheck(t.mesh.position)) {
        this.scene.remove(t.mesh);
        materialPool.release(t.mesh.material as THREE.MeshBasicMaterial);
        this.tracers.splice(i, 1);
        continue;
      }

      // Fade out
      (t.mesh.material as THREE.MeshBasicMaterial).opacity = 0.9 * (1 - t.age / TRACER_LIFETIME);
    }
  }

  private spawnTracer(aircraft: THREE.Object3D): void {
    _forward.set(0, 0, -1).applyQuaternion(aircraft.quaternion);
    _right.set(1, 0, 0).applyQuaternion(aircraft.quaternion);
    _up.set(0, 1, 0).applyQuaternion(aircraft.quaternion);

    const dir = _forward.clone();
    dir.addScaledVector(_right, (Math.random() - 0.5) * SPREAD);
    dir.addScaledVector(_up, (Math.random() - 0.5) * SPREAD);
    dir.normalize();

    const start = aircraft.position.clone().addScaledVector(_forward, 22);

    const material = materialPool.acquire();
    material.color.set(0xffdd44);
    material.opacity = 0.9;

    const mesh = new THREE.Mesh(sharedGeometry, material);
    mesh.position.copy(start);

    // Orient cylinder along direction of travel
    const lookTarget = start.clone().add(dir);
    mesh.lookAt(lookTarget);

    mesh.frustumCulled = false;
    this.scene.add(mesh);

    this.tracers.push({
      mesh,
      velocity: dir.multiplyScalar(TRACER_SPEED),
      age: 0,
    });
  }

  dispose(): void {
    for (const t of this.tracers) {
      this.scene.remove(t.mesh);
      materialPool.release(t.mesh.material as THREE.MeshBasicMaterial);
    }
    this.tracers.length = 0;
  }
}
